import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { PERMISSION_ACTIONS, permissionModuleName } from "@apv-erp/status";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { trpcClient } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth.store";

const actionsOf = (perms: any, mod: string): string[] => {
  const e = perms?.[mod];
  return Array.isArray(e?.actions) ? e.actions : [];
};

/**
 * Áp dụng mẫu quyền cho vai trò (04/11/2026): chọn mẫu → xem trước module / thao tác thay đổi → xác nhận.
 * Thay toàn bộ quyền của vai trò bằng mẫu; máy chủ ghi lịch sử quyền cũ → mới.
 */
export function ApplyPermissionTemplateDialog({
  open,
  onOpenChange,
  role,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: { roleId: string; name: string; permissions: any } | null;
}) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const { data: templates = [] } = useQuery({
    queryKey: ["permission-templates"],
    enabled: open,
    queryFn: () => (trpcClient as any).roles.listTemplates.query(),
  });
  const tpl: any = (templates as any[]).find((t) => t.code === code) || (templates as any[])[0] || null;

  // Xem trước: module được thêm / bớt quyền so với hiện tại
  const diff = useMemo(() => {
    if (!tpl || !role) return { gain: 0, lose: 0, rows: [] as Array<{ mod: string; add: string[]; remove: string[] }> };
    const cur = role.permissions || {};
    const next = tpl.permissions || {};
    const mods = Array.from(new Set([...Object.keys(cur), ...Object.keys(next)])).filter((m) => m !== "*");
    const rows = mods
      .map((m) => {
        const a = actionsOf(cur, m);
        const b = actionsOf(next, m);
        return { mod: m, add: b.filter((x) => !a.includes(x)), remove: a.filter((x) => !b.includes(x)) };
      })
      .filter((r) => r.add.length || r.remove.length);
    return {
      gain: rows.reduce((n, r) => n + r.add.length, 0),
      lose: rows.reduce((n, r) => n + r.remove.length, 0) + (cur["*"] ? 1 : 0),
      rows,
    };
  }, [tpl, role]);

  const label = (id: string) => PERMISSION_ACTIONS.find((a) => a.id === id)?.label || id;

  const apply = async () => {
    if (!tpl || !role) return;
    setSaving(true);
    try {
      await (trpcClient as any).roles.applyTemplate.mutate({ roleId: role.roleId, templateCode: tpl.code });
      toast.success(`Đã áp dụng mẫu "${tpl.name}" cho vai trò ${role.name}`);
      await queryClient.invalidateQueries({ queryKey: ["role", role.roleId] });
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      trpcClient.auth.me
        .query()
        .then((u: any) => u?.id && useAuthStore.getState().setUser(u))
        .catch(() => {});
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Không áp dụng được mẫu quyền");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="apply-template-dialog">
        <DialogHeader icon={ShieldCheck}>
          <DialogTitle>Áp dụng mẫu quyền cho {role?.name}</DialogTitle>
          <DialogDescription>Toàn bộ quyền hiện tại của vai trò sẽ được thay bằng mẫu. Có ghi lịch sử thay đổi.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5">
            {(templates as any[]).map((t) => (
              <label key={t.code} className="flex cursor-pointer items-start gap-2 rounded-md border bg-card p-2.5">
                <input type="radio" name="tpl" className="mt-1" checked={tpl?.code === t.code} onChange={() => setCode(t.code)} data-testid={`template-${t.code}`} />
                <span>
                  <span className="font-semibold text-foreground">{t.name}</span>
                  {t.description ? <span className="block text-xs text-muted-foreground">{t.description}</span> : null}
                </span>
              </label>
            ))}
            {!(templates as any[]).length && <p className="text-xs text-muted-foreground">Chưa có mẫu quyền.</p>}
          </div>
          {tpl && (
            <div className="rounded-md border bg-card p-2.5" data-testid="apply-template-preview">
              <p className="text-xs font-semibold text-foreground">
                Thay đổi: +{diff.gain} quyền, −{diff.lose} quyền{role?.permissions?.["*"] ? " (bỏ Toàn quyền)" : ""}
              </p>
              <ul className="mt-1.5 max-h-48 space-y-0.5 overflow-auto text-xs text-slate-600">
                {diff.rows.map((r) => (
                  <li key={r.mod}>
                    <b>{permissionModuleName(r.mod)}</b>
                    {r.add.length ? <span className="text-emerald-700"> +{r.add.map(label).join(", ")}</span> : null}
                    {r.remove.length ? <span className="text-rose-700"> −{r.remove.map(label).join(", ")}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Đóng
          </Button>
          <Button onClick={apply} disabled={!tpl || saving} data-testid="apply-template-confirm">
            {saving ? "Đang áp dụng..." : "Áp dụng mẫu"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
